import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LayoutDashboard,
  FileText,
  Scale,
  Bell,
  LogOut,
  Plus,
  Search,
  Filter,
  Clock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LegalDashboard({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('contracts');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [contracts, setContracts] = useState([]);
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAddContractDialogOpen, setIsAddContractDialogOpen] = useState(false);
  const [isAddCaseDialogOpen, setIsAddCaseDialogOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [newContract, setNewContract] = useState({
    company_id: '',
    title: '',
    description: '',
    contract_type: '',
    party_name: '',
    contract_value: '',
    notes: '',
  });

  const [newCase, setNewCase] = useState({
    company_id: '',
    case_number: '',
    title: '',
    description: '',
    case_type: '',
    plaintiff: '',
    defendant: '',
    court_name: '',
    notes: '',
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedCompany, statusFilter, searchTerm, activeTab]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error('فشل تحميل الشركات');
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/stats`, {
          params: { company_id: selectedCompany !== 'all' ? selectedCompany : undefined },
        }),
        axios.get(`${API}/notifications`),
      ]);

      setStats(statsRes.data);
      setNotifications(notificationsRes.data);

      if (activeTab === 'contracts') {
        const contractsRes = await axios.get(`${API}/contracts`, {
          params: {
            company_id: selectedCompany !== 'all' ? selectedCompany : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setContracts(contractsRes.data);
      } else {
        const casesRes = await axios.get(`${API}/cases`, {
          params: {
            company_id: selectedCompany !== 'all' ? selectedCompany : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setCases(casesRes.data);
      }
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    if (!newContract.company_id) {
      toast.error('الرجاء اختيار الشركة');
      return;
    }
    try {
      await axios.post(`${API}/contracts`, {
        ...newContract,
        contract_value: newContract.contract_value ? parseFloat(newContract.contract_value) : null,
      });
      toast.success('تم إضافة العقد بنجاح');
      setIsAddContractDialogOpen(false);
      setNewContract({
        company_id: '',
        title: '',
        description: '',
        contract_type: '',
        party_name: '',
        contract_value: '',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error('فشل إضافة العقد');
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    if (!newCase.company_id) {
      toast.error('الرجاء اختيار الشركة');
      return;
    }
    try {
      await axios.post(`${API}/cases`, newCase);
      toast.success('تم إضافة القضية بنجاح');
      setIsAddCaseDialogOpen(false);
      setNewCase({
        company_id: '',
        case_number: '',
        title: '',
        description: '',
        case_type: '',
        plaintiff: '',
        defendant: '',
        court_name: '',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error('فشل إضافة القضية');
    }
  };

  const handleStatusChange = async (id, newStatus, type) => {
    try {
      const endpoint = type === 'contract' ? 'contracts' : 'cases';
      await axios.patch(`${API}/${endpoint}/${id}`, { status: newStatus });
      toast.success(`تم تحديث حالة ${type === 'contract' ? 'العقد' : 'القضية'} بنجاح`);
      fetchData();
    } catch (error) {
      toast.error(`فشل تحديث حالة ${type === 'contract' ? 'العقد' : 'القضية'}`);
    }
  };

  const handleApproveContract = async (contractId) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/approve`);
      toast.success('تم اعتماد العقد بنجاح');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل اعتماد العقد');
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/${notificationId}/read`);
      fetchData();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    navigate('/login');
    toast.success('تم تسجيل الخروج بنجاح');
  };

  const getContractStatusBadge = (status) => {
    const statusMap = {
      draft: { text: 'مسودة', class: 'status-draft' },
      review: { text: 'قيد المراجعة', class: 'status-review' },
      approved: { text: 'معتمد', class: 'status-approved' },
      signed: { text: 'موقّع', class: 'status-signed' },
    };
    const statusInfo = statusMap[status] || statusMap.draft;
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const getCaseStatusBadge = (status) => {
    const statusMap = {
      new: { text: 'جديدة', class: 'status-new' },
      filed: { text: 'مرفوعة', class: 'status-filed' },
      hearings: { text: 'جلسات', class: 'status-hearings' },
      final_judgment: { text: 'حكم نهائي', class: 'status-final_judgment' },
      execution: { text: 'تنفيذ', class: 'status-execution' },
    };
    const statusInfo = statusMap[status] || statusMap.new;
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-64 bg-[#4a4a4a] text-slate-300 z-50">
        <div className="p-6">
          <img
            src="https://customer-assets.emergentagent.com/job_contract-portal-1/artifacts/qhuwq0xk_IFG%20logo.png"
            alt="IFG Logo"
            className="h-16 mb-8 mx-auto"
          />
          <nav className="space-y-2">
            <button
              className="sidebar-item active w-full text-right px-4 py-3 rounded-md flex items-center gap-3"
              data-testid="dashboard-nav-item"
            >
              <LayoutDashboard size={20} />
              <span>لوحة التحكم</span>
            </button>
            <button
              className="sidebar-item w-full text-right px-4 py-3 rounded-md flex items-center gap-3 relative"
              onClick={() => setIsNotificationsOpen(true)}
              data-testid="notifications-nav-item"
            >
              <Bell size={20} />
              <span>الإشعارات</span>
              {unreadCount > 0 && (
                <span className="absolute left-4 top-3 bg-[#E94B7B] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          </nav>
        </div>
        <div className="absolute bottom-0 w-full p-6 border-t border-slate-700">
          <div className="mb-4 text-sm">
            <p className="font-medium text-white">{user?.full_name}</p>
            <p className="text-xs text-slate-400">مدير الشؤون القانونية</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700"
            data-testid="logout-button"
          >
            <LogOut size={18} className="ml-2" />
            تسجيل الخروج
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="mr-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            مرحباً، {user?.full_name}
          </h1>
          <p className="text-slate-600">إدارة العقود والقضايا القانونية لمجموعة IFG</p>
        </div>

        {/* Company Selector */}
        <div className="mb-6">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-64 h-11 bg-white border-slate-200" data-testid="company-select">
              <SelectValue placeholder="اختر الشركة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع الشركات</SelectItem>
              {companies.map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name_ar}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <FileText className="text-slate-600" size={24} />
              <span className="text-3xl font-bold text-slate-900">{stats.contracts?.total || 0}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">إجمالي العقود</p>
          </div>
          <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-amber-600" size={24} />
              <span className="text-3xl font-bold text-amber-600">{stats.contracts?.review || 0}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">قيد المراجعة</p>
          </div>
          <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="text-emerald-600" size={24} />
              <span className="text-3xl font-bold text-emerald-600">{stats.contracts?.signed || 0}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">موقّعة</p>
          </div>
          <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Scale className="text-[#E94B7B]" size={24} />
              <span className="text-3xl font-bold text-[#E94B7B]">{stats.cases?.total || 0}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">إجمالي القضايا</p>
          </div>
          <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="text-blue-600" size={24} />
              <span className="text-3xl font-bold text-blue-600">{stats.cases?.hearings || 0}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">جلسات</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 mb-6">
          <div className="flex gap-8">
            <button
              onClick={() => {
                setActiveTab('contracts');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className={`tab-button ${activeTab === 'contracts' ? 'active' : ''}`}
              data-testid="contracts-tab"
            >
              <FileText size={18} className="inline-block ml-2" />
              العقود
            </button>
            <button
              onClick={() => {
                setActiveTab('cases');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className={`tab-button ${activeTab === 'cases' ? 'active' : ''}`}
              data-testid="cases-tab"
            >
              <Scale size={18} className="inline-block ml-2" />
              القضايا
            </button>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <Input
              placeholder={`البحث في ${activeTab === 'contracts' ? 'العقود' : 'القضايا'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-10 h-11 bg-white border-slate-200"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 h-11 bg-white border-slate-200" data-testid="filter-status-select">
              <Filter size={16} className="ml-2" />
              <SelectValue placeholder="تصفية حسب الحالة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع الحالات</SelectItem>
              {activeTab === 'contracts' ? (
                <>
                  <SelectItem value="draft">مسودة</SelectItem>
                  <SelectItem value="review">قيد المراجعة</SelectItem>
                  <SelectItem value="approved">معتمد</SelectItem>
                  <SelectItem value="signed">موقّع</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="new">جديدة</SelectItem>
                  <SelectItem value="filed">مرفوعة</SelectItem>
                  <SelectItem value="hearings">جلسات</SelectItem>
                  <SelectItem value="final_judgment">حكم نهائي</SelectItem>
                  <SelectItem value="execution">تنفيذ</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
          {activeTab === 'contracts' ? (
            <Dialog open={isAddContractDialogOpen} onOpenChange={setIsAddContractDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#E94B7B] hover:bg-[#d43a6a] text-white h-11 px-6 shadow-md"
                  data-testid="add-contract-button"
                >
                  <Plus size={20} className="ml-2" />
                  إضافة عقد جديد
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl" dir="rtl">
                <DialogHeader>
                  <DialogTitle className="text-2xl text-right">إضافة عقد جديد</DialogTitle>
                  <DialogDescription className="text-right">
                    أدخل تفاصيل العقد الجديد
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateContract} className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto px-1">
                  <div className="space-y-2">
                    <Label htmlFor="contract-company" className="text-right block">الشركة *</Label>
                    <Select
                      value={newContract.company_id}
                      onValueChange={(value) => setNewContract({ ...newContract, company_id: value })}
                    >
                      <SelectTrigger id="contract-company" className="text-right" data-testid="contract-company-select">
                        <SelectValue placeholder="اختر الشركة" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name_ar}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contract-title" className="text-right block">عنوان العقد *</Label>
                    <Input
                      id="contract-title"
                      value={newContract.title}
                      onChange={(e) => setNewContract({ ...newContract, title: e.target.value })}
                      required
                      className="text-right"
                      data-testid="contract-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contract-description" className="text-right block">الوصف *</Label>
                    <Textarea
                      id="contract-description"
                      value={newContract.description}
                      onChange={(e) => setNewContract({ ...newContract, description: e.target.value })}
                      required
                      rows={3}
                      className="text-right"
                      data-testid="contract-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contract-type" className="text-right block">نوع العقد *</Label>
                      <Input
                        id="contract-type"
                        value={newContract.contract_type}
                        onChange={(e) => setNewContract({ ...newContract, contract_type: e.target.value })}
                        required
                        className="text-right"
                        data-testid="contract-type-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contract-party" className="text-right block">الطرف الثاني *</Label>
                      <Input
                        id="contract-party"
                        value={newContract.party_name}
                        onChange={(e) => setNewContract({ ...newContract, party_name: e.target.value })}
                        required
                        className="text-right"
                        data-testid="contract-party-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contract-value" className="text-right block">قيمة العقد (اختياري)</Label>
                    <Input
                      id="contract-value"
                      type="number"
                      step="0.01"
                      value={newContract.contract_value}
                      onChange={(e) => setNewContract({ ...newContract, contract_value: e.target.value })}
                      className="text-right"
                      data-testid="contract-value-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contract-notes" className="text-right block">ملاحظات (اختياري)</Label>
                    <Textarea
                      id="contract-notes"
                      value={newContract.notes}
                      onChange={(e) => setNewContract({ ...newContract, notes: e.target.value })}
                      rows={2}
                      className="text-right"
                      data-testid="contract-notes-input"
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1 bg-[#E94B7B] hover:bg-[#d43a6a]" data-testid="submit-contract-button">
                      إضافة العقد
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsAddContractDialogOpen(false)}
                      className="flex-1"
                      data-testid="cancel-contract-button"
                    >
                      إلغاء
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          ) : (
            <Dialog open={isAddCaseDialogOpen} onOpenChange={setIsAddCaseDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#E94B7B] hover:bg-[#d43a6a] text-white h-11 px-6 shadow-md"
                  data-testid="add-case-button"
                >
                  <Plus size={20} className="ml-2" />
                  إضافة قضية جديدة
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl" dir="rtl">
                <DialogHeader>
                  <DialogTitle className="text-2xl text-right">إضافة قضية جديدة</DialogTitle>
                  <DialogDescription className="text-right">
                    أدخل تفاصيل القضية الجديدة
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateCase} className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto px-1">
                  <div className="space-y-2">
                    <Label htmlFor="case-company" className="text-right block">الشركة *</Label>
                    <Select
                      value={newCase.company_id}
                      onValueChange={(value) => setNewCase({ ...newCase, company_id: value })}
                    >
                      <SelectTrigger id="case-company" className="text-right" data-testid="case-company-select">
                        <SelectValue placeholder="اختر الشركة" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name_ar}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="case-number" className="text-right block">رقم القضية *</Label>
                      <Input
                        id="case-number"
                        value={newCase.case_number}
                        onChange={(e) => setNewCase({ ...newCase, case_number: e.target.value })}
                        required
                        className="text-right"
                        data-testid="case-number-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="case-type" className="text-right block">نوع القضية *</Label>
                      <Input
                        id="case-type"
                        value={newCase.case_type}
                        onChange={(e) => setNewCase({ ...newCase, case_type: e.target.value })}
                        required
                        className="text-right"
                        data-testid="case-type-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="case-title" className="text-right block">عنوان القضية *</Label>
                    <Input
                      id="case-title"
                      value={newCase.title}
                      onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
                      required
                      className="text-right"
                      data-testid="case-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="case-description" className="text-right block">الوصف *</Label>
                    <Textarea
                      id="case-description"
                      value={newCase.description}
                      onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
                      required
                      rows={3}
                      className="text-right"
                      data-testid="case-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="case-plaintiff" className="text-right block">المدعي *</Label>
                      <Input
                        id="case-plaintiff"
                        value={newCase.plaintiff}
                        onChange={(e) => setNewCase({ ...newCase, plaintiff: e.target.value })}
                        required
                        className="text-right"
                        data-testid="case-plaintiff-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="case-defendant" className="text-right block">المدعى عليه *</Label>
                      <Input
                        id="case-defendant"
                        value={newCase.defendant}
                        onChange={(e) => setNewCase({ ...newCase, defendant: e.target.value })}
                        required
                        className="text-right"
                        data-testid="case-defendant-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="case-court" className="text-right block">اسم المحكمة (اختياري)</Label>
                    <Input
                      id="case-court"
                      value={newCase.court_name}
                      onChange={(e) => setNewCase({ ...newCase, court_name: e.target.value })}
                      className="text-right"
                      data-testid="case-court-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="case-notes" className="text-right block">ملاحظات (اختياري)</Label>
                    <Textarea
                      id="case-notes"
                      value={newCase.notes}
                      onChange={(e) => setNewCase({ ...newCase, notes: e.target.value })}
                      rows={2}
                      className="text-right"
                      data-testid="case-notes-input"
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1 bg-[#E94B7B] hover:bg-[#d43a6a]" data-testid="submit-case-button">
                      إضافة القضية
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsAddCaseDialogOpen(false)}
                      className="flex-1"
                      data-testid="cancel-case-button"
                    >
                      إلغاء
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Data List */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : activeTab === 'contracts' ? (
            contracts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <FileText size={48} className="mb-4" />
                <p className="text-lg">لا توجد عقود</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">عنوان العقد</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">نوع العقد</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الطرف الثاني</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الحالة</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">تاريخ الإنشاء</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {contracts.map((contract) => (
                      <tr
                        key={contract.id}
                        className="hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/contract/${contract.id}`)}
                        data-testid={`contract-row-${contract.id}`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{contract.title}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{contract.contract_type}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{contract.party_name}</td>
                        <td className="px-6 py-4">{getContractStatusBadge(contract.status)}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {new Date(contract.created_at).toLocaleDateString('ar-SA')}
                        </td>
                        <td className="px-6 py-4">
                          {contract.status === 'draft' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(contract.id, 'review', 'contract');
                              }}
                              data-testid={`send-to-review-${contract.id}`}
                            >
                              إرسال للمراجعة
                            </Button>
                          )}
                          {contract.status === 'review' && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApproveContract(contract.id);
                              }}
                              data-testid={`approve-contract-${contract.id}`}
                            >
                              اعتماد
                            </Button>
                          )}
                          {contract.status === 'approved' && (
                            <Button
                              size="sm"
                              className="bg-[#E94B7B] hover:bg-[#d43a6a] text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/contract/${contract.id}`);
                              }}
                              data-testid={`sign-contract-${contract.id}`}
                            >
                              توقيع
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            cases.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Scale size={48} className="mb-4" />
                <p className="text-lg">لا توجد قضايا</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">رقم القضية</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">عنوان القضية</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">نوع القضية</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">المدعي</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الحالة</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">تاريخ الإنشاء</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {cases.map((caseItem) => (
                      <tr
                        key={caseItem.id}
                        className="hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/case/${caseItem.id}`)}
                        data-testid={`case-row-${caseItem.id}`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{caseItem.case_number}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{caseItem.title}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{caseItem.case_type}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{caseItem.plaintiff}</td>
                        <td className="px-6 py-4">{getCaseStatusBadge(caseItem.status)}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {new Date(caseItem.created_at).toLocaleDateString('ar-SA')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>

      {/* Notifications Dialog */}
      <Dialog open={isNotificationsOpen} onOpenChange={setIsNotificationsOpen}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-right">الإشعارات</DialogTitle>
            <DialogDescription className="text-right">
              آخر التحديثات والإشعارات
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-3 mt-4">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bell size={48} className="mx-auto mb-4 opacity-50" />
                <p>لا توجد إشعارات</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    notification.read
                      ? 'bg-slate-50 border-slate-200'
                      : 'bg-pink-50 border-pink-200'
                  }`}
                  onClick={() => {
                    markNotificationAsRead(notification.id);
                    if (notification.related_type === 'contract' && notification.related_id) {
                      navigate(`/contract/${notification.related_id}`);
                      setIsNotificationsOpen(false);
                    } else if (notification.related_type === 'case' && notification.related_id) {
                      navigate(`/case/${notification.related_id}`);
                      setIsNotificationsOpen(false);
                    }
                  }}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900 mb-1">{notification.title}</h4>
                      <p className="text-sm text-slate-600">{notification.message}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(notification.created_at).toLocaleString('ar-SA')}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-[#E94B7B] rounded-full mt-2" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
